import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  makeStyles,
} from '@material-ui/core';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PaymentIcon from '@mui/icons-material/Payment';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    boxShadow: '0 10px 36px rgba(15,23,42,0.16)',
    overflow: 'hidden',
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: '16px 20px',
    borderBottom: '1px solid #e8edf2',
    backgroundColor: '#fff',
  },
  dialogHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  headerIconWrapper: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#fff7ed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIcon: {
    fontSize: 22,
    color: '#ea580c',
  },
  headerTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: '#0f172a',
    lineHeight: 1.3,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: '#64748b',
    marginTop: 1,
  },
  closeButton: {
    color: '#94a3b8',
    padding: 6,
    borderRadius: 8,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: '#f1f5f9',
      color: '#334155',
    },
  },
  content: {
    padding: '24px 24px 16px',
    backgroundColor: '#f8fafc',
    [theme.breakpoints.down('sm')]: {
      padding: '20px 16px 12px',
    },
  },
  contentIconWrapper: {
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: '#fff7ed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  contentIcon: {
    fontSize: 34,
    color: '#f97316',
  },
  message: {
    textAlign: 'center',
    color: '#475569',
    lineHeight: 1.65,
    marginBottom: theme.spacing(1),
    fontSize: '0.9375rem',
  },
  highlight: {
    fontWeight: 700,
    color: '#0f172a',
  },
  dateBadge: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  dateChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 10,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#0f172a',
  },
  dateLabel: {
    color: '#64748b',
    fontWeight: 500,
  },
  helperMessage: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '0.875rem',
    lineHeight: 1.55,
    marginTop: theme.spacing(1.5),
  },
  actions: {
    justifyContent: 'center',
    padding: '8px 24px 24px',
    gap: 10,
    backgroundColor: '#f8fafc',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
      padding: '8px 16px 16px',
    },
  },
  button: {
    borderRadius: 10,
    textTransform: 'none',
    fontWeight: 600,
    minWidth: 140,
    height: 40,
  },
}));

/**
 * Modal de aviso de trial acabando.
 *
 * Props:
 *   trial: user.trial — { inTrial, expired, daysRemaining, expiresAt } (/auth/me)
 *   dueDate: fallback exibido quando trial.expiresAt não está disponível
 */
const TrialEndingModal = ({ open, onClose, trial, dueDate }) => {
  const classes = useStyles();
  const history = useHistory();

  const daysLeft = trial?.daysRemaining ?? null;
  const expiresAt = trial?.expiresAt ?? dueDate ?? null;
  const formattedDate = expiresAt ? moment(expiresAt).format('DD/MM/YYYY') : '';

  // daysRemaining = 0 → hoje é o último dia com acesso
  const daysLabel =
    daysLeft === null
      ? 'em breve'
      : daysLeft <= 0
        ? 'hoje'
        : `em ${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'}`;

  const handleGoToPlans = () => {
    onClose();
    history.push('/subscription/plans');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <Box className={classes.dialogHeader}>
        <Box className={classes.dialogHeaderLeft}>
          <Box className={classes.headerIconWrapper}>
            <ScheduleIcon className={classes.headerIcon} />
          </Box>
          <Box minWidth={0}>
            <Typography className={classes.headerTitle} component="h2">
              Período de teste acabando
            </Typography>
            <Typography className={classes.headerSubtitle} component="p">
              Escolha um plano para continuar sem interrupções.
            </Typography>
          </Box>
        </Box>
        <IconButton
          className={classes.closeButton}
          size="small"
          onClick={onClose}
          aria-label="Fechar modal"
        >
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent className={classes.content}>
        <Box className={classes.contentIconWrapper}>
          <ScheduleIcon className={classes.contentIcon} />
        </Box>
        <Typography className={classes.message}>
          Seu período de teste encerra <span className={classes.highlight}>{daysLabel}</span>.
        </Typography>
        {formattedDate && (
          <Box className={classes.dateBadge}>
            <Box className={classes.dateChip} component="span">
              <span className={classes.dateLabel}>Vencimento:</span>
              {formattedDate}
            </Box>
          </Box>
        )}
        <Typography className={classes.helperMessage}>
          Você pode adiar este aviso por hoje ou ir direto para a escolha de planos.
        </Typography>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button variant="outlined" onClick={onClose} className={classes.button}>
          Lembrar depois
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGoToPlans}
          className={classes.button}
          startIcon={<PaymentIcon fontSize="small" />}
        >
          Ver planos
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrialEndingModal;
